import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { TaskItemPrivacyType } from "src/constants";
import { useSignalR } from "src/hooks";
import { BulkCreateTaskItemsDto, FetchError, queryKeys, useAccessToken, UserDto } from "src/http";

type Variables = {
  taskItems: {
    taskItemGuid: string;
    teamSlug?: string;
    description: string;
    parentTaskItemGuid?: string;
    contextActionItemGuid?: string;
    parentIssueGuid?: string;
    note: string;
    dueDate: string | null;
    owners: UserDto[];
    primaryOwner: UserDto | null;
    createdFromSurveyResponseAnswerGuid?: string;
    privacy?: TaskItemPrivacyType;
  }[];
};

const useBulkCreateTaskItemsRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function bulkCreateTaskItemsRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: BulkCreateTaskItemsDto = {
      taskItems: variables.taskItems.map((taskItem) => ({
        guid: taskItem.taskItemGuid,
        teamSlug: taskItem.teamSlug,
        description: taskItem.description,
        parentTaskItemGuid: taskItem.parentTaskItemGuid,
        contextActionItemGuid: taskItem.contextActionItemGuid,
        parentIssueGuid: taskItem.parentIssueGuid,
        note: taskItem.note,
        dueDate: taskItem.dueDate,
        ownerUserIds: taskItem.owners?.map((x) => x.userId),
        primaryOwnerUserId: taskItem.primaryOwner?.userId,
        createdFromSurveyResponseAnswerGuid: taskItem.createdFromSurveyResponseAnswerGuid,
        privacy: taskItem.privacy,
      })),
    };

    const response = await fetch(`${configuration.apiRootUrl}/task-items/bulk`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }

    return response;
  }

  return { bulkCreateTaskItemsRequest };
};

const useBulkCreateTaskItems = () => {
  const { bulkCreateTaskItemsRequest } = useBulkCreateTaskItemsRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: bulkCreateTaskItemsRequest,
    onSuccess: (_data, variables) => {
      const parentTaskItemGuids = new Set<string>();
      const contextActionItemGuids = new Set<string>();
      const parentIssueGuids = new Set<string>();

      variables.taskItems.forEach((item) => {
        if (item.parentTaskItemGuid) {
          parentTaskItemGuids.add(item.parentTaskItemGuid);
        }
        if (item.contextActionItemGuid) {
          contextActionItemGuids.add(item.contextActionItemGuid);
        }
        if (item.parentIssueGuid) {
          parentIssueGuids.add(item.parentIssueGuid);
        }
      });

      parentTaskItemGuids.forEach((guid) => {
        signalR.sendRefetchTaskItem(guid);
      });

      contextActionItemGuids.forEach((guid) => {
        signalR.sendRefetchActionItem(guid);
      });

      parentIssueGuids.forEach((guid) => {
        signalR.sendRefetchIssue(guid);
      });

      signalR.sendRefetchTeamTaskItems();
      signalR.sendRefetchUserTaskItems();
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.taskItems.allTeamTaskItems);
      queryClient.invalidateQueries(queryKeys.taskItems.allTeamTaskItemSubtasks);
      queryClient.invalidateQueries(queryKeys.taskItems.allUserTaskItems);

      const parentTaskItemGuids = new Set<string>();
      const contextActionItemGuids = new Set<string>();
      const parentIssueGuids = new Set<string>();
      const createdFromSurveyResponseAnswerGuids = new Set<string>();

      variables.taskItems.forEach((item) => {
        if (item.parentTaskItemGuid) {
          parentTaskItemGuids.add(item.parentTaskItemGuid);
        }
        if (item.contextActionItemGuid) {
          contextActionItemGuids.add(item.contextActionItemGuid);
        }
        if (item.parentIssueGuid) {
          parentIssueGuids.add(item.parentIssueGuid);
        }
        if (item.createdFromSurveyResponseAnswerGuid) {
          createdFromSurveyResponseAnswerGuids.add(item.createdFromSurveyResponseAnswerGuid);
        }
      });

      parentTaskItemGuids.forEach((guid) => {
        queryClient.invalidateQueries(queryKeys.taskItems.getTaskItemSubtasks(guid));
      });

      // Update supporting items list
      contextActionItemGuids.forEach((guid) => {
        queryClient.invalidateQueries(queryKeys.actionItems.item(guid));
      });

      parentIssueGuids.forEach((issueguid) => {
        queryClient.invalidateQueries(queryKeys.taskItems.getIssueTaskItems(issueguid));
      });

      createdFromSurveyResponseAnswerGuids.forEach((guid) => {
        queryClient.invalidateQueries(queryKeys.surveys.getItemsCreatedFromResponseAnswer(guid));
      });
    },
  });
};

export { useBulkCreateTaskItems };
